<template>
  <div class="staff-alerts">
    <facebook-create-dialog
      :isVisible="showFacebookCreateDialog"
      :me="me"
      @onFacebookChannelAdded="() => handleChannelAdded('showFacebookCreateDialog')"
      @onVisibleChanged="value => handleDialogVisibilityChanged(value, handleFacebookVisibilityChange)"
    />

    <instagram-create-dialog
      :me="me"
      :isVisible="showInstagramCreateDialog"
      @onFacebookChannelAdded="refetchChannelList"
      @onVisibleChanged="value => handleDialogVisibilityChanged(value, handleInstagramVisibilityChange)"
    />

    <email-channel-create-dialog
      :me="me"
      :isVisible="showEmailCreateDialog"
      @onEmailChannelAdded="refetchChannelList"
      @onVisibleChanged="value => handleDialogVisibilityChanged(value, handleEmailDialogVisibilityChange)"
    />

    <wechat-channel-create-dialog
      :isVisible="showWechatCreateDialog"
      @onEmailChannelAdded="refetchChannelList"
      @onVisibleChanged="value => handleDialogVisibilityChanged(value, handleWechatDialogVisibilityChange)"
    />

    <whatsapp-web-channel-create-dialog
      v-if="showWhatsAppWebCreateDialog"
      :isVisible="showWhatsAppWebCreateDialog"
      :intialData="{ phoneNumber, selectedCountry }"
      @onWhatsappChannelAdded="() => handleChannelAdded('showWhatsAppWebCreateDialog')"
      @onVisibleChanged="value => handleDialogVisibilityChanged(value, handleWhatsappWebDialogVisibilityChange)"
    />  
  </div>  
</template>

<script>
import { mapGetters } from 'vuex';
import * as types from '@/store/modules/alerts/mutation-types';
import FacebookCreateDialog from '@/components/channel/facebook/FacebookCreateDialog';
import InstagramCreateDialog from '@/components/channel/instagram/InstagramCreateDialog';
import EmailChannelCreateDialog from '@/components/channel/email/EmailChannelCreateDialog';
import WechatChannelCreateDialog from '@/components/channel/wechat/WechatChannelCreateDialog';
import WhatsappWebChannelCreateDialog from '@/components/channel/whatsappweb/WhatsappWebChannelCreateDialog';
import countryCodesMixin from '@/components/common/countries/countryCodesMixin';
import channelCreateMixin from '@/components/channel/channelCreateMixin';

export default {
  name: 'StaffAlerts',

  mixins: [countryCodesMixin, channelCreateMixin],

  components: {
    WhatsappWebChannelCreateDialog,
    FacebookCreateDialog,
    InstagramCreateDialog,
    EmailChannelCreateDialog,
    WechatChannelCreateDialog,
  },
  computed: {
    ...mapGetters('alerts', ['getNextAlert', 'getAllAlerts']),
  },

  data() {
    return {
      activeAlert: null,
      getAlertsTime: 5 * 60 * 1000, // every 5 minutes
      getAlertsInterval: null,
      alertOpen: false,
      severityTypes: ['info', 'warning', 'error'],
      phoneNumber: null,
      selectedCountry: null,
    };
  },

  mounted() {
    this.getAlertsInterval = setInterval(
      vm => {
        vm.$store.dispatch('alerts/getStaffAlerts');
      },
      this.getAlertsTime,
      this,
    );

    this.$store.subscribe(mutation => {
      if (mutation.type === `alerts/${types.GET_STAFF_ALERTS_SUCCESS}`) {
        this.showNextAlert();
      }
    });
  },

  destroyed() {
    // clean up intervals
    clearInterval(this.getAlertsInterval);
    clearInterval(this.processAlertsInterval);
  },

  methods: {
    handleDialogVisibilityChanged(value, handler) {
      handler(value);

      if (!value) {
        this.alertOpen = false;
        this.showNextAlert();
      }
    },

    handleChannelAdded(visibilityProp) {
      if (this[visibilityProp]) this[visibilityProp] = false;
      if (this.alertOpen) this.alertOpen = false;

      // mark alert as read.
      this.markRead(this.activeAlert.id);
      this.refetchChannelList();
    },

    showAlert(alert, id) {
      this.activeAlert = alert;
      this.alertOpen = true;

      this.$msgbox({
        title: alert.title,
        message: alert.body,
        type: this.severityTypes[alert.severity],
        confirmButtonText: 'Read',
        cancelButtonText: alert.link === 'Channel' ? 'Authorise Channel' : null,
        cancelButtonClass: 'warning',
        showCancelButton: alert.link === 'Channel',
        distinguishCancelAndClose: true,
        showClose: false,
        closeOnClickModal: false,
        closeOnPressEscape: false,
      })
        .then(action => {
          if (action === 'confirm') {
            this.markRead(id);
            this.alertOpen = false;
            this.showNextAlert();
          }
        })
        .catch(action => {
          // Authorise Channel sets 'cancel' action.
          if (action === 'cancel' && alert.channel) {
            // WhatsApp channel
            if (alert.channel.channel_type === 9) {
              const filterNumber =
                alert.channel.handle[0] === '+' ? alert.channel.handle : `+${alert.channel.handle}`;
              const country = this.getCountryFromPhoneNumber(filterNumber);

              if (country) {
                this.phoneNumber = filterNumber.replace(country.dial_code, '');
                this.selectedCountry = { ...country };
              }
              this.showWhatsAppWebCreateDialog = true;
              return;
            }

            this.connectChannel(alert.channel.channel_type_title);

            if ([2, 12].includes(alert.channel.channel_type)) {
              this.alertOpen = false;
              this.showNextAlert();
            }
            return;
          }

          this.alertOpen = false;
          this.showNextAlert();
        });
    },

    isCreateDialogVisible() {
      /**
       * Check if any of the create channel dialogs are visible. This avoids overloading
       * conditional logic to check for each one explicitely.
       */

      // property names used for showing channel dialogs.
      const keys = [
        'showFacebookCreateDialog',
        'showInstagramCreateDialog',
        'showEmailCreateDialog',
        'showWechatCreateDialog',
        'showWhatsAppWebCreateDialog',
      ];

      return keys.reduce((a, x) => {
        if (a) return a;
        return this[x];
      }, false);
    },

    showNextAlert() {
      if (!this.alertOpen && !this.isCreateDialogVisible()) {
        this.$store.commit(`alerts/${types.GET_NEXT_STAFF_ALERT}`);
        const alert = this.getNextAlert;
        if (alert) {
          if (this.activeAlert && this.activeAlert.id === alert.id) {
            this.showNextAlert();
            return;
          }
          this.showAlert(alert.alert, alert.id);
        }
      }
    },

    markRead(id) {
      this.$store.dispatch('alerts/updateStaffAlertAsRead', id);
    },
  },
};
</script>



// WEBPACK FOOTER //
// StaffAlerts.vue?88a51e2a